import _getLongestLine from "../utility/getLongestLine.js";
import _findCenter from "../utility/findCenter.js";
import _convertToPlainText from "../quill/convertToPlainText.js";
const convertToPlainText = _convertToPlainText;
const findCenter = _findCenter;
const getLongestLine = _getLongestLine;
export default {
  replaceColorTags: function (text) {
    return text.replace(/<c\.(\w+)>(.*?)<\/c>/g, '<$1>$2</$1>');
  },
  calcSize: function (event, win) {
    let paddingSize = Math.floor(win.xPadding / 100 * win.width);
    let maxLength = win.width + paddingSize * 2;
    let plainText = convertToPlainText(event.text);
    let longestLine = getLongestLine(plainText);
    let size = parseInt(longestLine.length / maxLength * 100);
    return Math.min(size, 90);
  },
  calcPos: function (event, win, size) {
    let xPos = event.xPos;
    // Calculate position percentage based on flexbox value
    if (xPos === "start") {
      return win.xPadding;
    } else if (xPos === "end") {
      return 100 - win.xPadding - size;
    } else if (xPos === "center") {
      return 50;
    }

    // Convert integer xPos to flexbox value if needed
    if (typeof xPos === 'number') {
      const xPercentage = Math.max(10, xPos / win.width * 100);
      return xPercentage;
    }
  },
  calcLineValue: function (event, numberOfLines, win) {
    /* 
    
    We need to update the line value calculation based on the new position system used by Closed Caption Creator. We need a way to translate the event yPos value to a % value for webVTT. The Line value in a webVtt file controls the **vertical position** of the cue (subtitle) box. In Closed Caption Creator we store the vertical position as either a flexbox value ("start", "center", "end") or as an integer value representing a line offset from the top of the display window. In webVTT the line value is a percentage from 0% (top of the display) to 100% (bottom of the display). In Closed Caption Creator, the window also has a yPadding value that needs to be taken into account when calculating the line value for webVTT. In VTT files, each subtitle line is approximately 5.33% of the display height. This means we can fit roughly 19 lines of text on the display. 
    
    Therefore, to calculate the line value for webVTT, we need to consider the following:
     
    1. If the event.yPos value is set to "start" we can simply return the window's yPadding value as the line value.
    2. If the event.yPos value is set to "center" we need to calculate the center position of the display minus half the height of the subtitle box (numberOfLines * 5.33%).
    3. If the event.yPos value is set to "end" we need to calculate the bottom position of the display minus the height of the subtitle box (numberOfLines * 5.33%) minus the window's yPadding value.
    4. If the event.yPos value is an integer, we need to convert that integer to a percentage based on the window height and return that as the line value. To do this we'll need to calculate the full height of the display window including the yPadding, then convert the integer to a percentage of that height. We'll also need to convert yPadding to text lines and add that to the yPos before calculating the percentage.        
    */

    let yPos = event.yPos;
    const lineHeight = 5.33; // Each subtitle line is approximately 5.33% of display height
    const yPadding = win?.yPadding || 10; // Window's vertical padding (in %)
    const windowHeight = win?.height || 15; // Default window height in lines
    const yPaddingInLines = Math.floor(yPadding / 100) * windowHeight;
    const fullDisplayHeight = windowHeight + yPaddingInLines * 2; //*2 for top and bottom padding

    // Convert integer yPos to percentage
    if (typeof yPos === 'number') {
      // Convert yPadding from lines to its position in the line count
      const adjustedYPos = yPos + yPaddingInLines;
      // Calculate percentage based on total window height
      const yPercentage = adjustedYPos / fullDisplayHeight * 100;
      return Math.min(Math.max(yPercentage, 0), 100);
    }

    // Handle flexbox values with yPadding consideration
    let line = 94.66; // Default to bottom

    if (yPos === "start") {
      // Start yPadding
      line = yPadding;
    } else if (yPos === "center") {
      // Center the subtitle box vertically
      line = findCenter(100, numberOfLines * lineHeight);
    } else if (yPos === "end") {
      // Bottom position - subtitle height - yPadding
      line = 100 - yPadding - numberOfLines * lineHeight;
    }
    return Math.min(Math.max(line, 0), 100);
  },
  calcVlcLineValue: function (event, numberOfLines, win) {
    /* 
        We need to rewrite this function to calculate the VLC line value based on the new position system used by Closed Caption Creator. The VLC line value controls the vertical position of the subtitle box in VLC media player. In Closed Caption Creator we store the vertical position as either a flexbox value ("start", "center", "end") or as an integer value representing a line offset from the top of the display window. In VLC, the line value ranges from 1 (top of the screen) to 19 (bottom of the screen). In order to calculate the correct VLC line value we need to look at the window's height (in lines), window's yPadding (%), the number of text lines, and the event's yPos value.
          1. Start by converting the window's yPadding from % to lines.
        2. Calculate the total height of the display window including the yPadding in lines
        3. If the event.yPos value is set to "start" we can return 1 + the yPadding in lines
        4. If the event.yPos value is set to "center" we need to calculate the center position of the display minus half the height of the subtitle box (numberOfLines / 2).
        5. If the event.yPos value is set to "end" we need to calculate the bottom position of the display minus the height of the subtitle box (numberOfLines) and the yPadding in lines.
        6. If the event.yPos value is an integer, we need to convert that integer to a VLC line value based on the window height and return that as the line value. For example, if the yPos value is 2 and the window height is 15 lines, we would calculate the VLC line value as follows: (yPos + yPaddingInLines) / fullDisplayHeight * 19
        7. Ensure the final line value is clamped between 1 and 19.
    */

    let line = 19; // Default value is 19 (bottom of the screen)
    let yPos = event.yPos;
    try {
      const windowHeight = win?.height || 15; // Default window height in lines
      const yPadding = win?.yPadding || 10; // Window's vertical padding in %

      // Step 1: Convert yPadding from % to lines
      const yPaddingInLines = Math.floor(yPadding / 100 * windowHeight);

      // Step 2: Calculate total display height including padding
      const fullDisplayHeight = windowHeight + yPaddingInLines * 2; // *2 for top and bottom padding

      // Step 6: Handle integer yPos values
      if (typeof yPos === 'number') {
        // Convert integer position to VLC line value (1-19 range)
        const adjustedYPos = yPos + yPaddingInLines;
        line = Math.round(adjustedYPos / fullDisplayHeight * 19);
      }
      // Step 3: Handle "start" position
      else if (yPos === "start") {
        line = 1 + yPaddingInLines;
      }
      // Step 4: Handle "center" position
      else if (yPos === "center") {
        // Center position minus half the subtitle box height
        const centerLine = Math.round(fullDisplayHeight / 2);
        line = centerLine - Math.round(numberOfLines / 2);
      }
      // Step 5: Handle "end" position
      else if (yPos === "end") {
        // Bottom position minus subtitle box height minus yPadding
        line = fullDisplayHeight - yPaddingInLines - numberOfLines;
      }

      // Step 7: Clamp line value between 1 and 19
      line = Math.min(19, Math.max(1, line));
      return parseInt(line);
    } catch (error) {
      console.log(error);
      return parseInt(line);
    }
  },
  webcargoLineValue: function (event, numberOfLines, win) {
    let yPos = event.yPos;
    const lineHeight = 5.33; // Each subtitle line is approximately 5.33% of display height
    const yPadding = win?.yPadding || 10; // Window's vertical padding (in %)
    const windowHeight = win?.height || 15; // Default window height in lines
    const yPaddingInLines = Math.floor(yPadding / 100) * windowHeight;
    const fullDisplayHeight = windowHeight + yPaddingInLines * 2; //*2 for top and bottom padding

    // Convert integer yPos to percentage
    if (typeof yPos === 'number') {
      // Convert yPadding from lines to its position in the line count
      const adjustedYPos = yPos + yPaddingInLines;
      // Calculate percentage based on total window height
      const yPercentage = adjustedYPos / fullDisplayHeight * 100;
      return Math.min(Math.max(yPercentage, 0), 100);
    }

    // Handle flexbox values with yPadding consideration
    let line = 94.66; // Default to bottom

    if (yPos === "start") {
      // Start yPadding
      line = yPadding;
    } else if (yPos === "center") {
      // Center the subtitle box vertically
      line = findCenter(100, numberOfLines * lineHeight);
    } else if (yPos === "end") {
      // Bottom position - subtitle height - yPadding
      line = 100 - yPadding - numberOfLines * lineHeight;
    }
    return Math.min(Math.max(line, 0), 100);
  },
  /**
  * Converts HTML color spans to VTT color cues
  * @param {string} htmlText - The input HTML text containing color spans
  * @returns {string} - The converted text with VTT color cues
  */
  convertHtmlToVttColors: function (htmlText) {
    // Helper function to normalize color names
    function normalizeColor(color) {
      // Remove any whitespace and convert to lowercase
      color = color.toLowerCase().trim();

      // Convert some common color variations
      const colorMap = {
        '#ffff00': 'yellow',
        '#ffffff': 'white',
        '#ff0000': 'red',
        '#008000': 'lime',
        '#0000ff': 'blue',
        '#ff00ff': 'magenta',
        '#00ffff': 'cyan',
        '#000000': 'black'
      };

      // Remove '#' if present and check in colorMap
      if (color.startsWith('#')) {
        const mapped = colorMap[color];
        if (mapped) return mapped;
      }
      return color;
    }

    // Regular expression to match span tags with color styles
    const spanRegex = /<span\s+style=['"]color:\s*([^'"]+);['"]>(.*?)<\/span>/gi;

    // Replace each span with corresponding VTT cue
    let result = htmlText.replace(spanRegex, (match, color, content) => {
      const normalizedColor = normalizeColor(color) || color;
      return `<c.${normalizedColor}>${content}</c>`;
    });
    return result;
  },
  alignmentMap: {
    "left": "left",
    "start": "left",
    "middle": "center",
    "center": "center",
    "right": "right",
    "end": "right"
  },
  colorMapping: {
    "#000000": "black",
    //black
    "#FF0000": "red",
    //red
    "#008000": "lime",
    //lime
    "#FFFF00": "yellow",
    //yellow
    "#0000FF": "blue",
    //blue
    "#FF00FF": "magenta",
    //magenta
    "#00FFFF": "cyan",
    //cyan
    "#FFFFFF": "white" //white
  },
  fontStyleCues: `
STYLE
::cue(.black) {
    color: #000000;
}
::cue(.red) {
    color: #FF0000;
}
::cue(.lime) {
    color: #008000;
}
::cue(.yellow) {
    color: #FFFF00;
}
::cue(.blue) {
    color: #0000FF;
}
::cue(.magenta) {
    color: #FF00FF;
}
::cue(.cyan) {
    color: #00FFFF;
}
::cue(.white) {
    color: #FFFFFF;
}
::cue(.bg_black) {
    background-color: #000000;
}
::cue(.bg_red) {
    background-color: #FF0000;
}
::cue(.bg_lime) {
    background-color: #008000;
}
::cue(.bg_yellow) {
    background-color: #FFFF00;
}
::cue(.bg_blue) {
    background-color: #0000FF;
}
::cue(.bg_magenta) {
    background-color: #FF00FF;
}
::cue(.bg_cyan) {
    background-color: #00FFFF;
}
::cue(.bg_white) {
    background-color: #FFFFFF;
}`
};